// ZatcaSettingsPage.jsx — الربط مع هيئة الزكاة والضريبة والجمارك (المرحلة الثانية)
// بالتصميم الموحّد. حالة الربط (نشط/موقوف) هنا هي المرجع الذي تعتمد عليه الفواتير.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Alert, CircularProgress, Switch,
  ToggleButton, ToggleButtonGroup, Tooltip, Collapse,
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import axios from '../../axios';
import { ReportPage, fieldSx, BRAND } from '../../components/Reports/ReportKit';

const EMPTY = {
  environmentType: 'Simulation',
  commonName: '',
  organizationUnitName: '',
  organizationName: '',
  registeredAddress: '',
  businessCategory: '',
  invoiceType: '1100',
  otp: '',
  isActive: false,
  isCsrGenerated: false,
  isComplianceChecked: false,
  isProductionOnboarded: false,
  lastCheckDate: null,
  activatedAt: null,
};

// 1100 = الاثنتان، 1000 = قياسية فقط (B2B)، 0100 = مبسطة فقط (B2C)
const INVOICE_TYPES = [
  { value: '1100', label: 'قياسية ومبسطة', hint: 'للشركات والأفراد (B2B و B2C)' },
  { value: '1000', label: 'قياسية فقط', hint: 'فواتير الشركات (B2B)' },
  { value: '0100', label: 'مبسطة فقط', hint: 'فواتير الأفراد ونقاط البيع (B2C)' },
];

// ── بيانات الشهادة من بطاقة المنشأة ──
// حروف إنجليزية وأرقام فقط (متطلب CSR)، والمسافات تصبح "-"
const asciiPart = (v, max = 30) => String(v || '')
  .normalize('NFKD').replace(/[^A-Za-z0-9 _.-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, max);
const onlyDigits = (v) => String(v || '').replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660)).replace(/\D/g, '');

/**
 * الاسم الشائع (CN): اسم فريد لوحدة الفوترة — [اسم المنشأة بالإنجليزي][-فرع]-[رقم الهوية]-[الرقم الضريبي]
 * مثال: ARBAH-1010123456-310123456700003  /  ARBAH-B2-1010123456-310123456700003
 * الوحدة التنظيمية (OU): اسم الفرع، أو أول 10 أرقام من الرقم الضريبي إذا كان رقم مجموعة ضريبية (الخانة 11 = 1)
 */
function buildFromProfile(p, { branch } = {}) {
  const vat = onlyDigits(p.vatNo);
  const idNo = onlyDigits(p.crn) || asciiPart(p.crn, 20);
  const prefix = asciiPart(p.nameEn, 20) || 'EGS';
  const unit = branch ? `-B${branch.id}` : '';
  const commonName = [`${prefix}${unit}`, idNo, vat].filter(Boolean).join('-');

  const isGroupVat = vat.length === 15 && vat[10] === '1';
  const organizationUnitName = isGroupVat
    ? vat.slice(0, 10)
    : asciiPart(branch ? (branch.nameEn || branch.name) : p.nameEn, 30) || (branch ? `Branch-${branch.id}` : 'Main-Branch');

  const address = [p.buildingNo, p.streetName, p.district, p.city, p.postalCode]
    .map(x => String(x || '').trim()).filter(Boolean).join(', ') || String(p.address || '').trim();

  return {
    commonName,
    organizationUnitName,
    organizationName: p.nameAr || p.nameEn || '',
    registeredAddress: address,
    businessCategory: p.activityEn || p.activityAr || '',
    missing: [
      !vat && 'الرقم الضريبي',
      vat && vat.length !== 15 && 'الرقم الضريبي (15 رقمًا)',
      !idNo && 'رقم السجل/الهوية',
      !p.nameEn && 'الاسم بالإنجليزي',
    ].filter(Boolean),
  };
}

const errorText = (err, fallback) => {
  const d = err?.response?.data;
  const missing = Array.isArray(d?.missingFields) && d.missingFields.length ? ` — الحقول الناقصة: ${d.missingFields.join('، ')}` : '';
  return (d?.message ? d.message + missing : '') || d?.title || (typeof d === 'string' ? d : '') || err?.message || fallback;
};

function Card({ title, icon, color = BRAND.primary, action, children }) {
  return (
    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: `${color}1a`, color, '& svg': { fontSize: 18 } }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
    </Paper>
  );
}

function StepCard({ n, title, desc, done, current, locked, icon, children }) {
  const color = done ? '#059669' : current ? BRAND.primary : '#94a3b8';
  return (
    <Box sx={{
      display: 'flex', gap: 1.5, p: 1.5, borderRadius: '14px',
      border: `1.5px solid ${done ? '#a7f3d0' : current ? BRAND.primaryLight : '#e5e7eb'}`,
      bgcolor: done ? '#f0fdf4' : current ? '#fafaff' : '#fff',
      opacity: locked ? .6 : 1,
    }}>
      <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '11px', display: 'grid', placeItems: 'center', color: '#fff', bgcolor: color, fontWeight: 800 }}>
        {done ? <CheckCircleIcon fontSize="small" /> : icon || n}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: done ? '#047857' : '#0f172a' }}>{n}. {title}</Typography>
        {desc && <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: .25, lineHeight: 1.7 }}>{desc}</Typography>}
        {children && <Box sx={{ mt: 1 }}>{children}</Box>}
      </Box>
    </Box>
  );
}

export default function ZatcaSettingsPage() {
  const [scope, setScope] = useState('company');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [s, setS] = useState(EMPTY);
  const [loaded, setLoaded] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);         // اسم العملية الجارية
  const [testResults, setTestResults] = useState([]);

  const branchParam = scope === 'branch' ? (branchId || null) : null;
  const ready = scope === 'company' || !!branchId;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const dirty = ['environmentType', 'commonName', 'organizationUnitName', 'organizationName', 'registeredAddress', 'businessCategory', 'invoiceType']
    .some(k => (s[k] ?? '') !== (loaded[k] ?? ''));
  const certLocked = s.isCsrGenerated;   // البيئة ونوع الفواتير لا يتغيران إلا بإعادة توليد CSR

  useEffect(() => {
    axios.get('/Branch').then(r => setBranches(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const r = await axios.get('/ZatcaSettings', { params: branchParam ? { branchId: branchParam } : {} });
      const next = { ...EMPTY, ...(r.data || {}) };
      ['commonName', 'organizationUnitName', 'organizationName', 'registeredAddress', 'businessCategory', 'otp'].forEach(k => { next[k] = next[k] || ''; });
      setS(next);
      setLoaded(next);
      setTestResults([]);
    } catch (e) {
      toast.error(errorText(e, 'تعذر تحميل إعدادات الربط'));
    } finally {
      setLoading(false);
    }
  }, [ready, branchParam]);

  useEffect(() => { load(); }, [load]);

  const run = async (name, fn, success) => {
    setBusy(name);
    try {
      const res = await fn();
      if (success) toast.success(typeof success === 'function' ? success(res) : success);
      return res;
    } catch (e) {
      toast.error(errorText(e, 'فشلت العملية'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const body = () => ({ ...s, branchId: branchParam });

  const save = () => run('save', () => axios.post('/ZatcaSettings', body()), 'تم حفظ الإعدادات').then(r => r && load());

  const fetchProfile = () => {
    if (s.isCsrGenerated && !window.confirm('سيتم استبدال بيانات الشهادة الحالية ببيانات بطاقة المنشأة. التغيير لا يؤثر على الشهادة الحالية حتى تعيد توليد CSR. متابعة؟')) return;
    run('profile', async () => {
      const companyId = localStorage.getItem('companyId');
      const { data: p } = await axios.get(`/CompanyProfile/${companyId}`);
      const branch = scope === 'branch' ? branches.find(b => String(b.id) === String(branchId)) : null;
      const built = buildFromProfile(p || {}, { branch });
      setS(prev => ({
        ...prev,
        commonName: built.commonName || prev.commonName,
        organizationUnitName: built.organizationUnitName || prev.organizationUnitName,
        organizationName: built.organizationName || prev.organizationName,
        registeredAddress: built.registeredAddress || prev.registeredAddress,
        businessCategory: built.businessCategory || prev.businessCategory,
      }));
      if (built.missing.length) toast.warn(`أكمل في بطاقة المنشأة: ${built.missing.join('، ')}`);
    }, 'تم جلب البيانات من بطاقة المنشأة — راجعها ثم احفظ');
  };

  const generateCsr = () => {
    if (s.isProductionOnboarded && !window.confirm('إعادة توليد CSR تُلغي الربط الحالي وشهادة الإنتاج، وتحتاج إعادة الربط من البداية (OTP جديد). هل تريد المتابعة؟')) return;
    run('csr', () => axios.post('/ZatcaSettings/generate-csr', body()), 'تم توليد CSR والمفاتيح').then(r => r && load());
  };

  const compliance = () => {
    if (!s.otp.trim()) { toast.warn('أدخل رمز OTP من بوابة فاتورة'); return; }
    run('otp', () => axios.post('/ZatcaSettings/onboard-compliance', body()), 'تم الحصول على شهادة الامتثال (Compliance CSID)').then(r => r && load());
  };

  const tests = () => run('tests', () => axios.post('/ZatcaSettings/run-compliance-checks', { branchId: branchParam }))
    .then(r => {
      if (!r) return;
      setTestResults(r.data?.results || []);
      if (r.data?.success) toast.success('نجحت جميع اختبارات الامتثال');
      else toast.warn(r.data?.message || 'فشل بعض الاختبارات');
    });

  const production = () => run('prod', () => axios.post('/ZatcaSettings/onboard-production', { branchId: branchParam }), 'تم الربط الإنتاجي — الربط نشط').then(r => r && load());

  const resetOnboarding = () => {
    const msg = s.isProductionOnboarded
      ? 'سيتم مسح شهادة الإنتاج وإيقاف الربط، ولن تُرسل الفواتير للهيئة حتى تعيد الربط كاملًا بـ OTP جديد. هل أنت متأكد؟'
      : 'سيتم مسح CSR والمفاتيح وشهادة الامتثال، وتبدأ الربط من الخطوة الأولى. بيانات المنشأة لا تُمسح. متابعة؟';
    if (!window.confirm(msg)) return;
    run('reset', () => axios.post('/ZatcaSettings/reset', { branchId: branchParam }), r => r.data?.message || 'تم المسح').then(r => r && load());
  };

  const toggleActive = (isActive) => {
    if (!isActive && !window.confirm('إيقاف الربط يعني أن الفواتير لن تُرسل للهيئة عند اعتمادها، ويصبح تعديلها ممكنًا. هل تريد الإيقاف؟')) return;
    run('active', () => axios.post('/ZatcaSettings/set-active', { branchId: branchParam, isActive }), r => r.data?.message).then(r => r && load());
  };

  const step = s.isProductionOnboarded ? 4 : s.isComplianceChecked ? 3 : s.isCsrGenerated ? 2 : 1;
  const isProdEnv = s.environmentType === 'Production';
  const spin = (name) => (busy === name ? <CircularProgress size={16} color="inherit" /> : null);
  const btnSx = { borderRadius: '10px', fontWeight: 800, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } };

  return (
    <ReportPage
      title="الربط مع هيئة الزكاة والضريبة"
      subtitle="الفوترة الإلكترونية — المرحلة الثانية (الربط والتكامل)"
      icon={<VerifiedUserOutlinedIcon />}
      badges={[
        { label: s.isActive ? 'الربط نشط' : 'الربط غير نشط', icon: s.isActive ? <LinkIcon /> : <LinkOffIcon /> },
        { label: isProdEnv ? 'بيئة الإنتاج' : s.environmentType === 'Sandbox' ? 'بيئة المطورين' : 'بيئة المحاكاة', icon: isProdEnv ? <RocketLaunchOutlinedIcon /> : <ScienceOutlinedIcon /> },
      ]}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: load, disabled: loading || !ready },
        { label: busy === 'save' ? 'جاري الحفظ...' : 'حفظ الإعدادات', icon: spin('save') || <SaveOutlinedIcon />, onClick: save, primary: true, disabled: !!busy || loading || !ready || !dirty },
      ]}
    >
      {/* النطاق + حالة الربط */}
      <Paper elevation={0} sx={{ mb: 1.5, p: { xs: 1.5, sm: 2 }, borderRadius: '16px', border: '1px solid #e5e7eb', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup exclusive size="small" value={scope} onChange={(_, v) => v && setScope(v)}
          sx={{ '& .MuiToggleButton-root': { px: 2, gap: .75, fontWeight: 800, borderRadius: '10px !important', border: '1px solid #e5e7eb !important', mx: .25 },
                '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important` } }}>
          <ToggleButton value="company"><BusinessOutlinedIcon fontSize="small" /> مستوى الشركة</ToggleButton>
          <ToggleButton value="branch"><StorefrontOutlinedIcon fontSize="small" /> مستوى الفرع</ToggleButton>
        </ToggleButtonGroup>
        {scope === 'branch' && (
          <TextField select size="small" label="الفرع" value={branchId} onChange={e => setBranchId(e.target.value)} sx={{ ...fieldSx, minWidth: 220 }}>
            {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name || b.nameAr}</MenuItem>)}
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        {ready && !loading && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, pr: 1, py: .5, borderRadius: '12px',
            border: `1.5px solid ${s.isActive ? '#86efac' : '#e5e7eb'}`, bgcolor: s.isActive ? '#f0fdf4' : '#f8fafc',
          }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: s.isActive ? '#047857' : '#475569' }}>
                {s.isActive ? 'الربط نشط — الفواتير تُرسل للهيئة' : s.isProductionOnboarded ? 'الربط موقوف' : 'لم يكتمل الربط بعد'}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                {s.isActive ? 'تعديل الفواتير المعتمدة وإلغاء اعتمادها مقفل' : 'الفواتير لا تُرسل للهيئة'}
              </Typography>
              {s.activatedAt && (
                <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                  تاريخ الربط: <b style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{new Date(s.activatedAt).toLocaleString('en-GB')}</b>
                  {' '}— الفواتير قبله لا تُرسل
                </Typography>
              )}
            </Box>
            <Tooltip title={s.isProductionOnboarded ? '' : 'يتفعّل بعد الحصول على شهادة الإنتاج'}>
              <span>
                <Switch color="success" checked={!!s.isActive} disabled={!s.isProductionOnboarded || !!busy}
                  onChange={e => toggleActive(e.target.checked)} inputProps={{ 'aria-label': 'تفعيل الربط' }} />
              </span>
            </Tooltip>
          </Box>
        )}
      </Paper>

      {!ready ? (
        <Alert severity="info" sx={{ borderRadius: '12px' }}>اختر الفرع لعرض إعدادات الربط الخاصة به. الفروع بلا إعدادات خاصة تستخدم إعداد الشركة.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, alignItems: 'start', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' } }}>
          {/* ═════ بيانات الشهادة ═════ */}
          <Card title="بيانات الشهادة (CSR)" icon={<KeyOutlinedIcon />}
            action={
              <Button size="small" variant="outlined" onClick={fetchProfile} disabled={!!busy} startIcon={spin('profile') || <DownloadOutlinedIcon />}
                sx={{ fontWeight: 700, color: BRAND.primary, bgcolor: '#fff', borderColor: BRAND.primaryLight, borderRadius: '9px', whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: BRAND.primarySoft, borderColor: BRAND.primary }, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>
                من بطاقة المنشأة
              </Button>
            }>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField select size="small" label="بيئة العمل" value={s.environmentType} disabled={certLocked}
                onChange={e => set('environmentType', e.target.value)} sx={fieldSx}
                helperText={certLocked ? 'لتغييرها: "البدء من جديد" في خطوات الربط' : ' '}>
                <MenuItem value="Sandbox">بيئة المطورين (Sandbox) — OTP: 123456</MenuItem>
                <MenuItem value="Simulation">المحاكاة (Simulation)</MenuItem>
                <MenuItem value="Production">الإنتاج (Production)</MenuItem>
              </TextField>
              <TextField select size="small" label="أنواع الفواتير" value={s.invoiceType} disabled={certLocked}
                onChange={e => set('invoiceType', e.target.value)} sx={fieldSx}
                helperText={INVOICE_TYPES.find(t => t.value === s.invoiceType)?.hint || ' '}>
                {INVOICE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="الاسم الشائع (Common Name)" value={s.commonName} onChange={e => set('commonName', e.target.value)}
                sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} inputProps={{ dir: 'ltr' }}
                helperText="اسم فريد لوحدة الفوترة بحروف إنجليزية وأرقام: الاسم-رقم الهوية-الرقم الضريبي" />
              <TextField size="small" label="اسم المنظمة (O)" value={s.organizationName} onChange={e => set('organizationName', e.target.value)} sx={fieldSx} />
              <TextField size="small" label="الوحدة التنظيمية (OU)" value={s.organizationUnitName} onChange={e => set('organizationUnitName', e.target.value)} sx={fieldSx}
                helperText="اسم الفرع، أو الرقم الضريبي للمجموعات" />
              <TextField size="small" label="العنوان المسجل" value={s.registeredAddress} onChange={e => set('registeredAddress', e.target.value)} sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} />
              <TextField size="small" label="النشاط (Business Category)" value={s.businessCategory} onChange={e => set('businessCategory', e.target.value)} sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} />
            </Box>
            {dirty && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '12px', py: 0 }}>
                تغييرات غير محفوظة — احفظ قبل توليد CSR.
              </Alert>
            )}
          </Card>

          {/* ═════ خطوات الربط ═════ */}
          <Card title="خطوات الربط" icon={<VerifiedUserOutlinedIcon />} color="#059669"
            action={s.isCsrGenerated && (
              <Button size="small" variant="outlined" onClick={resetOnboarding} disabled={!!busy}
                startIcon={spin('reset') || <RestartAltIcon />}
                sx={{ fontWeight: 700, color: '#dc2626', bgcolor: '#fff', borderColor: '#fca5a5', borderRadius: '9px', whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' }, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>
                البدء من جديد
              </Button>
            )}>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <StepCard n={1} title="توليد طلب الشهادة (CSR)" icon={<KeyOutlinedIcon fontSize="small" />}
                done={s.isCsrGenerated} current={step === 1}
                desc={s.isCsrGenerated ? 'تم توليد المفاتيح وطلب الشهادة. إعادة التوليد تُلغي كل الخطوات التالية.' : 'يولّد المفاتيح وطلب الشهادة من البيانات المحفوظة.'}>
                <Button size="small" variant={s.isCsrGenerated ? 'outlined' : 'contained'} onClick={generateCsr} disabled={!!busy || dirty}
                  startIcon={spin('csr') || <KeyOutlinedIcon />} sx={{ ...btnSx, ...(s.isCsrGenerated ? {} : { background: BRAND.buttonGradient }) }}>
                  {s.isCsrGenerated ? 'إعادة توليد CSR' : 'توليد CSR'}
                </Button>
              </StepCard>

              <StepCard n={2} title="شهادة الامتثال (OTP)" icon={<PinOutlinedIcon fontSize="small" />}
                done={s.isComplianceChecked} current={step === 2} locked={!s.isCsrGenerated}
                desc={s.environmentType === 'Sandbox'
                  ? 'بيئة المطورين: رمز OTP الثابت 123456 (يُرسل للهيئة ويعطي شهادة اختبار حقيقية).'
                  : `أدخل رمز OTP من بوابة فاتورة — ${isProdEnv ? 'بيئة الإنتاج' : 'بيئة المحاكاة'}. الرمز صالح لمدة قصيرة.`}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {s.environmentType === 'Sandbox' && !s.otp && s.isCsrGenerated && (
                    <Button size="small" onClick={() => set('otp', '123456')} sx={{ fontWeight: 800, color: BRAND.primary }}>123456</Button>
                  )}
                  <TextField size="small" label="رمز OTP" value={s.otp} onChange={e => set('otp', e.target.value.replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660)))}
                    disabled={!s.isCsrGenerated} inputProps={{ dir: 'ltr', inputMode: 'numeric', style: { letterSpacing: 3, fontWeight: 800, textAlign: 'center' } }}
                    sx={{ ...fieldSx, width: 160 }} />
                  <Button size="small" variant="contained" onClick={compliance} disabled={!!busy || !s.isCsrGenerated || !s.otp.trim()}
                    startIcon={spin('otp') || <PinOutlinedIcon />} sx={{ ...btnSx, background: BRAND.buttonGradient }}>
                    طلب شهادة الامتثال
                  </Button>
                </Box>
              </StepCard>

              <StepCard n={3} title="اختبارات الامتثال" icon={<FactCheckOutlinedIcon fontSize="small" />}
                done={testResults.length > 0 && testResults.every(r => r.success)} current={step === 3} locked={!s.isComplianceChecked}
                desc={`ترسل فواتير اختبار (${s.invoiceType === '1000' ? 'قياسية' : s.invoiceType === '0100' ? 'مبسطة' : 'قياسية ومبسطة'}: فاتورة ودائن ومدين) حسب أنواع الفواتير في الشهادة. تتطلب بيانات العنوان كاملة في بطاقة المنشأة.`}>
                <Button size="small" variant="outlined" onClick={tests} disabled={!!busy || !s.isComplianceChecked}
                  startIcon={spin('tests') || <FactCheckOutlinedIcon />} sx={btnSx}>
                  تشغيل الاختبارات
                </Button>
                <Collapse in={testResults.length > 0}>
                  <Box sx={{ display: 'grid', gap: .5, mt: 1 }}>
                    {testResults.map(r => (
                      <Box key={r.key || r.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: .75, fontSize: 13 }}>
                        {r.success ? <CheckCircleIcon sx={{ fontSize: 18, color: '#059669' }} /> : <CancelIcon sx={{ fontSize: 18, color: '#dc2626' }} />}
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{r.name}</Typography>
                          {!r.success && <Typography sx={{ fontSize: 12, color: '#b91c1c' }}>{r.errorDetails || r.status || r.message}</Typography>}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </StepCard>

              <StepCard n={4} title="شهادة الإنتاج (تفعيل الربط)" icon={<RocketLaunchOutlinedIcon fontSize="small" />}
                done={s.isProductionOnboarded} current={step === 3} locked={!s.isComplianceChecked}
                desc={s.isProductionOnboarded
                  ? `تم الربط${s.lastCheckDate ? ` — آخر تحديث ${new Date(s.lastCheckDate).toLocaleDateString('en-CA')}` : ''}. الفواتير تعتمد على مفتاح "الربط نشط" بالأعلى.`
                  : 'بعد نجاح الاختبارات: يصدر شهادة الإنتاج ويفعّل الربط.'}>
                {!s.isProductionOnboarded && (
                  <Button size="small" variant="contained" color="success" onClick={production} disabled={!!busy || !s.isComplianceChecked}
                    startIcon={spin('prod') || <RocketLaunchOutlinedIcon />} sx={btnSx}>
                    إصدار شهادة الإنتاج
                  </Button>
                )}
              </StepCard>
            </Box>

            {!isProdEnv && s.isProductionOnboarded && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: '12px' }}>
                الربط على <b>البيئة التجريبية</b> — الفواتير لا تُعتمد رسميًا لدى الهيئة. للانتقال للإنتاج: غيّر البيئة بإعادة توليد CSR ثم أعد الخطوات بـ OTP من بوابة الإنتاج.
              </Alert>
            )}
          </Card>
        </Box>
      )}
    </ReportPage>
  );
}